/* eslint-disable camelcase */
import React from 'react';

import PropTypes from 'prop-types';

import getBuilderStateContentVariantData from '~/builder/utils/getBuilderStateContentVariantData';
import DesktopGridLayout from '~/gridv1_1/components/DesktopGridLayout';
import MobileGridLayout from '~/gridv1_1/components/MobileGridLayout';
import { ProductsV2Provider } from '~/gridv1_1/context/ProductsV2Provider';
import { GRID_TYPES, GridInputProvider } from '~/gridv1_1/hooks/useGridInputs';
import { useBreakpoint } from '~/techstyle-shared/react-components';

/**
 * Notes: Support for sidenav has been removed for this grid version, per the
 * side nav refactor. It is now only supported for Constructor Grids.
 *
 * Note: not all builder props are used directly via the refactored grid. Some
 * props (e.g. `brand`, `maxItemCount`, `sortList`) are passed via the builderBlock
 * component options and consumed via the grid hooks. See `getBuilderCollectionContent()`
 * in `useProductsV2()` for more details.
 */
const BuilderProductsV2GridV1_1 = ({
  builderBlock,
  builderState,
  filters,
  fplGlobalCodes,
  gridAssetContainer,
  gridAssetContainerMobile,
  gridCTA,
  gridGender,
  gridStyleGuide,
  productSource,
  label,
  productsGridV2gridV1LayoutSpacingOverride,
  productsGridV2gridV1HeaderSpacingOverride,
  excludeTagIdsToOmit,
}) => {
  const { isMobile } = useBreakpoint();

  // This is to prevent content shift between SSR and CSR hydration
  if (typeof window === 'undefined') {
    return null;
  }

  const pageData = getBuilderStateContentVariantData({
    builderStateContent: builderState?.content,
  });
  const gridSpotlights = pageData?.gridSpotlights?.value?.data;

  return (
    <GridInputProvider
      value={{
        assetContainer: {
          gridAssetContainer,
          gridAssetContainerMobile,
        },
        builderBlock,
        builderState,
        builderModel: builderState?.state?.modelName,
        filters,
        fplGlobalCodes,
        gridCTA,
        gridGender,
        gridHeader: label,
        gridSpotlights,
        gridStyleGuide,
        gridType: GRID_TYPES.PRODUCTS_V2_GRID,
        productSource,
        productsGridV2gridV1LayoutSpacingOverride,
        productsGridV2gridV1HeaderSpacingOverride,
        excludeTagIdsToOmit,
      }}
    >
      <ProductsV2Provider>
        {isMobile ? <MobileGridLayout /> : <DesktopGridLayout />}
      </ProductsV2Provider>
    </GridInputProvider>
  );
};

BuilderProductsV2GridV1_1.propTypes = {
  builderBlock: PropTypes.object,
  builderState: PropTypes.shape({
    state: PropTypes.object,
    content: PropTypes.object,
  }),
  /** Use this prop for `excludeTagIds` that should be omitted from exclusion. Provide an array tag IDs that should be omitted. **/
  excludeTagIdsToOmit: PropTypes.arrayOf(PropTypes.number),
  filters: PropTypes.arrayOf(PropTypes.shape({ filter: PropTypes.string })),
  fplGlobalCodes: PropTypes.arrayOf(PropTypes.string),
  gridAssetContainer: PropTypes.string,
  gridAssetContainerMobile: PropTypes.string,
  gridCTA: PropTypes.shape({
    label: PropTypes.string,
    url: PropTypes.string,
  }),
  gridGender: PropTypes.string,
  gridStyleGuide: PropTypes.object,
  label: PropTypes.string,
  productSource: PropTypes.string,
  productsGridV2gridV1HeaderSpacingOverride: PropTypes.PropTypes.arrayOf(
    PropTypes.object
  ),
  productsGridV2gridV1LayoutSpacingOverride: PropTypes.PropTypes.arrayOf(
    PropTypes.object
  ),
};

export default BuilderProductsV2GridV1_1;
